import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Brain,
  Sparkles,
  ListChecks,
  Camera,
  Loader2,
  RefreshCw,
  Check,
  X,
  Plus,
  Lock,
  AlertCircle,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { API_BASE } from '../config/api';
import { cn } from '../lib/utils';
import ElementsEditor, { elementsFromScene } from '../components/breakdown/ElementsEditor';

const API = API_BASE;

/* --------------------------- helpers --------------------------- */

const SHOT_TYPE_TONE = {
  WS:           'bg-primary/10 text-primary',
  ESTABLISHING: 'bg-primary/10 text-primary',
  MS:           'bg-info/10 text-info',
  CU:           'bg-warning/10 text-warning',
  ECU:          'bg-warning/15 text-warning',
  OTS:          'bg-chart-4/10 text-chart-4',
  TWO_SHOT:     'bg-success/10 text-success',
  INSERT:       'bg-muted text-muted-foreground',
  POV:          'bg-destructive/10 text-destructive',
  TRACKING:     'bg-chart-4/10 text-chart-4',
};

const natKey = (s) => {
  const v = String(s ?? '');
  const m = /^(\d+)/.exec(v);
  return m ? [parseInt(m[1], 10), v.slice(m[0].length)] : [Infinity, v];
};

const sortScenes = (scenes) =>
  [...scenes].sort((a, b) => {
    const [an, ax] = natKey(a.scene_number);
    const [bn, bx] = natKey(b.scene_number);
    if (an !== bn) return an - bn;
    return ax.localeCompare(bx);
  });

/* --------------------------- chip + section primitives --------------------------- */

const SectionHeader = ({ icon: Icon, label, count, accent = 'text-foreground' }) => (
  <div className="flex items-center gap-2 mb-2.5">
    <Icon className={cn('size-4', accent)} />
    <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
      {label}
    </span>
    {typeof count === 'number' && (
      <span className="text-[11px] text-muted-foreground">· {count}</span>
    )}
  </div>
);

/* --------------------------- suggested-shot card --------------------------- */

const ShotSuggestionCard = ({ suggestion, onAccept, onReject, busy }) => {
  const accepted = suggestion.status === 'accepted';
  const rejected = suggestion.status === 'rejected';
  const tone = SHOT_TYPE_TONE[suggestion.shot_type] || 'bg-secondary text-foreground';
  return (
    <div
      data-testid={`shot-suggestion-${suggestion.id}`}
      className={cn(
        'group rounded-xl border bg-card p-4 transition-all',
        accepted && 'border-success/30 bg-success/5',
        rejected && 'border-border bg-muted/30 opacity-60',
        !accepted && !rejected && 'border-border hover:border-primary/30',
      )}
    >
      <div className="flex items-start gap-3">
        {/* Shot-type pill */}
        <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
          <span className={cn('px-2 py-0.5 rounded text-[11px] font-mono font-semibold', tone)}>
            {suggestion.shot_type || 'SHOT'}
          </span>
          <span className="text-[10px] text-muted-foreground font-mono">{suggestion.shot_code}</span>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground leading-snug">
            {suggestion.description}
          </p>
          {(suggestion.camera_angle || suggestion.camera_movement) && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {suggestion.camera_angle && (
                <span className="text-[11px] text-muted-foreground bg-secondary/60 rounded px-1.5 py-0.5">
                  Angle: {suggestion.camera_angle}
                </span>
              )}
              {suggestion.camera_movement && (
                <span className="text-[11px] text-muted-foreground bg-secondary/60 rounded px-1.5 py-0.5">
                  Move: {suggestion.camera_movement}
                </span>
              )}
            </div>
          )}
          {suggestion.reasoning && (
            <p className="mt-2 text-[12px] italic text-muted-foreground leading-snug border-l-2 border-border pl-2">
              {suggestion.reasoning}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center gap-1">
          {accepted ? (
            <span className="inline-flex items-center gap-1 text-success text-[12px] font-medium">
              <Check className="size-3.5" /> Added
            </span>
          ) : rejected ? (
            <span className="text-muted-foreground text-[12px]">Rejected</span>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onReject(suggestion.id)}
                disabled={busy}
                title="Reject"
                data-testid={`shot-suggestion-reject-${suggestion.id}`}
              >
                <X className="size-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => onAccept(suggestion.id)}
                disabled={busy}
                title="Add to shot list"
                data-testid={`shot-suggestion-accept-${suggestion.id}`}
              >
                <Plus className="size-4 mr-1" />
                Add
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* --------------------------- main page --------------------------- */

export default function AIBreakdown() {
  const { filmId, workspaceId } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace, openUpgradeModal } = useWorkspace();

  const [film, setFilm] = useState(null);
  const [filmLoading, setFilmLoading] = useState(true);
  const [breakdown, setBreakdown] = useState(null);
  const [bdLoading, setBdLoading] = useState(true);
  const [bdError, setBdError] = useState(null);

  const [selectedSceneId, setSelectedSceneId] = useState(null);
  const [aiStatus, setAiStatus] = useState({ configured: false, model: null });
  const [entitlement, setEntitlement] = useState({ allowed: true, loaded: false });

  // Per-scene action state
  const [analyzing, setAnalyzing] = useState({});   // sceneId -> bool
  const [suggesting, setSuggesting] = useState({}); // sceneId -> bool
  const [actingOnSuggestion, setActingOnSuggestion] = useState({}); // suggestionId -> bool

  // Suggestions per scene
  const [suggestionsByScene, setSuggestionsByScene] = useState({});
  const [suggestionsLoading, setSuggestionsLoading] = useState({});

  // AI re-analysis entitlement (defaults to true until we know — checked via
  // /api/entitlements/film/{id}). Phase 1 Task 3 made can_upload_script true
  // for Free workspaces, so this page must gate on the PLAN-TIER-based
  // can_use_ai_reanalysis entitlement instead (analyze / suggest-shots stay
  // Pro/Studio-only on the backend).
  const canReanalyze = entitlement.allowed;

  /* ---------------- data fetch ---------------- */

  const fetchFilm = useCallback(async () => {
    setFilmLoading(true);
    try {
      const { data } = await axios.get(`${API}/films/${filmId}`, {
        headers: getAuthHeaders(),
      });
      setFilm(data);
    } catch (e) {
      console.error('Failed to load film', e);
    } finally {
      setFilmLoading(false);
    }
  }, [filmId]);

  const fetchBreakdown = useCallback(async () => {
    setBdLoading(true);
    setBdError(null);
    try {
      const { data } = await axios.get(`${API}/films/${filmId}/ai/breakdown`, {
        headers: getAuthHeaders(),
      });
      const sortedScenes = sortScenes(data.scenes || []);
      setBreakdown({ ...data, scenes: sortedScenes });
      // Auto-select the first scene if none is selected
      if (sortedScenes.length > 0) {
        setSelectedSceneId((prev) => prev ?? sortedScenes[0].scene_id);
      }
    } catch (e) {
      console.error('Failed to load AI breakdown', e);
      setBdError(e?.response?.data?.detail || 'Failed to load AI breakdown.');
    } finally {
      setBdLoading(false);
    }
  }, [filmId]);

  const fetchAIStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai/script-intelligence/status`, {
        headers: getAuthHeaders(),
      });
      setAiStatus(data);
    } catch (e) {
      console.error('Failed to load AI status', e);
    }
  }, []);

  const fetchSuggestions = useCallback(async (sceneId) => {
    if (!sceneId) return;
    setSuggestionsLoading((prev) => ({ ...prev, [sceneId]: true }));
    try {
      const { data } = await axios.get(
        `${API}/films/${filmId}/scenes/${sceneId}/ai/suggested-shots`,
        { headers: getAuthHeaders() }
      );
      setSuggestionsByScene((prev) => ({
        ...prev,
        [sceneId]: data.suggestions || [],
      }));
    } catch (e) {
      console.error('Failed to load suggestions', e);
    } finally {
      setSuggestionsLoading((prev) => ({ ...prev, [sceneId]: false }));
    }
  }, [filmId]);

  /* ---------------- effects ---------------- */

  useEffect(() => {
    fetchFilm();
    fetchAIStatus();
    fetchBreakdown();
  }, [fetchFilm, fetchAIStatus, fetchBreakdown]);

  // Entitlement check — uses the existing /api/entitlements/film/{id} endpoint
  useEffect(() => {
    let cancelled = false;
    const checkEntitlement = async () => {
      try {
        const { data } = await axios.get(`${API}/entitlements/film/${filmId}`, {
          headers: getAuthHeaders(),
        });
        if (cancelled) return;
        const ent = data?.entitlements || {};
        // Prefer the explicit tier-based re-analysis entitlement; fall back to
        // deriving it (upload allowed AND no free AI scene cap = paid tier)
        // for older backend payloads.
        const allowed = ent.can_use_ai_reanalysis
          ? !!ent.can_use_ai_reanalysis.allowed
          : (!!ent.can_upload_script?.allowed && data?.free_ai_scene_cap == null);
        setEntitlement({ allowed, loaded: true });
      } catch (e) {
        // On error, default to allowed (fail-open here is OK because the
        // backend re-checks the entitlement on every endpoint call).
        if (cancelled) return;
        setEntitlement({ allowed: true, loaded: true });
      }
    };
    if (filmId) checkEntitlement();
    return () => { cancelled = true; };
  }, [filmId]);

  useEffect(() => {
    if (selectedSceneId) {
      fetchSuggestions(selectedSceneId);
    }
  }, [selectedSceneId, fetchSuggestions]);

  /* ---------------- actions ---------------- */

  const handleAnalyzeScene = useCallback(async (sceneId) => {
    if (!canReanalyze) {
      openUpgradeModal && openUpgradeModal('script_upload');
      return;
    }
    setAnalyzing((prev) => ({ ...prev, [sceneId]: true }));
    try {
      const { data } = await axios.post(
        `${API}/films/${filmId}/scenes/${sceneId}/ai/analyze`,
        {},
        { headers: getAuthHeaders() }
      );
      if (data.success && data.breakdown) {
        toast.success('Scene analyzed');
        // Patch the scene in the local breakdown
        setBreakdown((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            scenes: prev.scenes.map((s) =>
              s.scene_id === sceneId
                ? {
                    ...s,
                    characters: data.breakdown.characters || [],
                    props: data.breakdown.props || [],
                    locations: data.breakdown.locations || [],
                    elements: data.breakdown.elements || {},
                    key_actions: data.breakdown.key_actions || [],
                    summary: data.breakdown.summary || '',
                    has_breakdown: true,
                  }
                : s
            ),
          };
        });
      } else {
        toast.error(data.error || 'AI analysis failed.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'AI analysis failed.');
    } finally {
      setAnalyzing((prev) => ({ ...prev, [sceneId]: false }));
    }
  }, [filmId, canReanalyze, openUpgradeModal]);

  const handleSuggestShots = useCallback(async (sceneId) => {
    if (!canReanalyze) {
      openUpgradeModal && openUpgradeModal('script_upload');
      return;
    }
    setSuggesting((prev) => ({ ...prev, [sceneId]: true }));
    try {
      const { data } = await axios.post(
        `${API}/films/${filmId}/scenes/${sceneId}/ai/suggest-shots`,
        {},
        { headers: getAuthHeaders() }
      );
      if (data.success) {
        setSuggestionsByScene((prev) => ({
          ...prev,
          [sceneId]: data.suggestions || [],
        }));
        toast.success(`Generated ${data.suggestions?.length || 0} shot suggestions`);
      } else {
        toast.error(data.error || 'Shot suggestion failed.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Shot suggestion failed.');
    } finally {
      setSuggesting((prev) => ({ ...prev, [sceneId]: false }));
    }
  }, [filmId, canReanalyze, openUpgradeModal]);

  const handleAcceptSuggestion = useCallback(async (sceneId, suggestionId) => {
    setActingOnSuggestion((prev) => ({ ...prev, [suggestionId]: true }));
    try {
      const { data } = await axios.post(
        `${API}/films/${filmId}/scenes/${sceneId}/ai/suggested-shots/${suggestionId}/accept`,
        {},
        { headers: getAuthHeaders() }
      );
      if (data.success) {
        toast.success('Shot added to scene');
        // Update local state
        setSuggestionsByScene((prev) => ({
          ...prev,
          [sceneId]: (prev[sceneId] || []).map((s) =>
            s.id === suggestionId ? { ...s, status: 'accepted', accepted_shot_id: data.shot_id } : s
          ),
        }));
      } else {
        toast.error(data.error || 'Failed to add shot');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add shot');
    } finally {
      setActingOnSuggestion((prev) => ({ ...prev, [suggestionId]: false }));
    }
  }, [filmId]);

  const handleRejectSuggestion = useCallback(async (sceneId, suggestionId) => {
    setActingOnSuggestion((prev) => ({ ...prev, [suggestionId]: true }));
    try {
      const { data } = await axios.post(
        `${API}/films/${filmId}/scenes/${sceneId}/ai/suggested-shots/${suggestionId}/reject`,
        {},
        { headers: getAuthHeaders() }
      );
      if (data.success) {
        setSuggestionsByScene((prev) => ({
          ...prev,
          [sceneId]: (prev[sceneId] || []).map((s) =>
            s.id === suggestionId ? { ...s, status: 'rejected' } : s
          ),
        }));
      }
    } catch (e) {
      toast.error('Failed to reject');
    } finally {
      setActingOnSuggestion((prev) => ({ ...prev, [suggestionId]: false }));
    }
  }, [filmId]);

  const handleAcceptAll = useCallback(async (sceneId) => {
    setSuggesting((prev) => ({ ...prev, [sceneId]: true }));
    try {
      const { data } = await axios.post(
        `${API}/films/${filmId}/scenes/${sceneId}/ai/suggested-shots/accept-all`,
        {},
        { headers: getAuthHeaders() }
      );
      if (data.success) {
        toast.success(`Added ${data.created_shot_ids?.length || 0} shots to the scene`);
        await fetchSuggestions(sceneId);
      } else {
        toast.error(data.error || 'Failed to add shots');
      }
    } catch (e) {
      toast.error('Failed to add shots');
    } finally {
      setSuggesting((prev) => ({ ...prev, [sceneId]: false }));
    }
  }, [filmId, fetchSuggestions]);

  /* ---------------- derived ---------------- */

  const selectedScene = useMemo(() => {
    if (!breakdown || !selectedSceneId) return null;
    return breakdown.scenes.find((s) => s.scene_id === selectedSceneId) || null;
  }, [breakdown, selectedSceneId]);

  const selectedSuggestions = suggestionsByScene[selectedSceneId] || [];
  const pendingSuggestionCount = selectedSuggestions.filter((s) => s.status === 'pending').length;

  const backHref = useMemo(() => {
    if (workspaceId) return `/workspaces/${workspaceId}/films/${filmId}`;
    return `/films/${filmId}`;
  }, [workspaceId, filmId]);

  /* ---------------- guards ---------------- */

  // Wait for entitlement check to complete before showing locked screen.
  // Free workspaces land here: viewing/re-running per-scene AI analysis is
  // Pro/Studio-only (their free taste lives in the script-upload flow).
  if (entitlement.loaded && !canReanalyze) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-3xl">
        <Link to={backHref} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="aib-back">
          <ArrowLeft className="size-4" /> Back to Film
        </Link>
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center" data-testid="aib-locked">
          <span className="inline-flex size-14 rounded-xl bg-secondary items-center justify-center mb-4">
            <Lock className="size-6 text-muted-foreground" />
          </span>
          <p className="font-display text-2xl font-semibold tracking-tight">AI Script Intelligence is a Pro Feature</p>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Extract characters, props, locations, and AI-suggested shot lists for every scene.
            Available on Pro and Studio plans.
          </p>
          <Button
            className="rounded-xl mt-5"
            onClick={() => (openUpgradeModal ? openUpgradeModal('script_upload') : navigate('/billing'))}
            data-testid="aib-upgrade-btn"
          >
            Upgrade to Pro
          </Button>
        </div>
      </div>
    );
  }

  if (!aiStatus.configured) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-3xl">
        <Link to={backHref} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="size-4" /> Back to Film
        </Link>
        <div className="rounded-2xl border border-dashed border-warning/30 bg-warning/5 p-10 text-center" data-testid="aib-not-configured">
          <AlertCircle className="size-8 text-warning mx-auto mb-3" />
          <p className="font-display text-xl font-semibold">AI Service Not Configured</p>
          <p className="text-sm text-muted-foreground mt-2">
            The AI Script Intelligence service is not configured on the server.
            Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  /* ---------------- render ---------------- */

  return (
    <div className="container mx-auto py-6 md:py-10 px-4 max-w-7xl" data-testid="ai-breakdown-page">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-6 md:mb-8">
        <Link to={backHref} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground w-fit" data-testid="aib-back">
          <ArrowLeft className="size-4" /> Back to Film
        </Link>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-chart-4 mb-1.5 inline-flex items-center gap-2">
              <Sparkles className="size-3" /> AI Script Intelligence
            </p>
            <h1 className="font-display text-3xl md:text-5xl font-semibold tracking-tight">
              {filmLoading ? <Skeleton className="h-10 w-72" /> : (film?.title || 'Untitled Film')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Production-ready breakdowns for every scene — characters, props, locations, and shot lists.
            </p>
          </div>
          {breakdown && (
            <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
              <span className="font-mono px-2 py-1 rounded bg-secondary/60">
                {breakdown.analyzed_count}/{breakdown.scenes.length} ANALYZED
              </span>
              <span className="font-mono px-2 py-1 rounded bg-primary/10 text-primary">
                {breakdown.total_characters} CHARACTERS
              </span>
              <span className="font-mono px-2 py-1 rounded bg-warning/10 text-warning">
                {breakdown.total_props} PROPS
              </span>
              <span className="font-mono px-2 py-1 rounded bg-info/10 text-info">
                {breakdown.total_locations} LOCATIONS
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Body — 2 column layout */}
      {bdLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
          <Skeleton className="h-[600px] rounded-2xl" />
          <Skeleton className="h-[600px] rounded-2xl" />
        </div>
      ) : bdError ? (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center">
          <AlertCircle className="size-6 text-destructive mx-auto mb-2" />
          <p className="text-sm text-destructive">{bdError}</p>
        </div>
      ) : !breakdown || breakdown.scenes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center" data-testid="aib-empty">
          <FileText className="size-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-display text-xl font-semibold">No scenes yet</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Add scenes to this film (or import a script) and we&apos;ll generate AI breakdowns for each one.
          </p>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to={backHref}>Go to Film Dashboard</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
          {/* Scene list sidebar */}
          <aside
            className="rounded-2xl border border-border bg-card/40 overflow-hidden h-fit md:sticky md:top-4"
            data-testid="aib-scene-list"
          >
            <div className="px-4 py-3 border-b border-border bg-card/60">
              <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
                Scenes · {breakdown.scenes.length}
              </p>
            </div>
            <ScrollArea className="h-[60vh]">
              <ul className="py-1">
                {breakdown.scenes.map((s) => {
                  const active = s.scene_id === selectedSceneId;
                  return (
                    <li key={s.scene_id}>
                      <button
                        type="button"
                        onClick={() => setSelectedSceneId(s.scene_id)}
                        data-testid={`aib-scene-item-${s.scene_id}`}
                        className={cn(
                          'w-full text-left px-4 py-2.5 flex items-start gap-2.5 transition-colors',
                          active ? 'bg-primary/10' : 'hover:bg-muted/50'
                        )}
                      >
                        <span className={cn(
                          'font-mono text-xs mt-0.5 font-semibold',
                          active ? 'text-primary' : 'text-muted-foreground'
                        )}>
                          {s.scene_number}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-foreground truncate leading-tight">
                            {s.scene_name || s.raw_slugline || `Scene ${s.scene_number}`}
                          </span>
                          <span className="block text-[11px] text-muted-foreground mt-0.5 truncate">
                            {s.raw_slugline || '—'}
                          </span>
                          <span className="mt-1 flex items-center gap-1.5">
                            {s.has_breakdown && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-success">
                                <Check className="size-2.5" /> Analyzed
                              </span>
                            )}
                            {s.pending_suggested_shot_count > 0 && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-warning">
                                <Camera className="size-2.5" /> {s.pending_suggested_shot_count} shots
                              </span>
                            )}
                          </span>
                        </span>
                        <ChevronRight className={cn(
                          'size-3.5 mt-1 flex-shrink-0',
                          active ? 'text-primary' : 'text-muted-foreground/50'
                        )} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
          </aside>

          {/* Scene breakdown content */}
          <section className="space-y-5" data-testid="aib-scene-detail">
            {selectedScene ? (
              <>
                <SceneHeaderCard
                  scene={selectedScene}
                  filmId={filmId}
                  workspaceId={workspaceId}
                  isAnalyzing={!!analyzing[selectedScene.scene_id]}
                  onAnalyze={() => handleAnalyzeScene(selectedScene.scene_id)}
                />

                <BreakdownContent
                  scene={selectedScene}
                  filmId={filmId}
                  canEdit={film?.can_edit !== false}
                  onElementsSaved={(els) => setBreakdown((prev) => prev && ({
                    ...prev,
                    scenes: prev.scenes.map((s) => s.scene_id === selectedScene.scene_id
                      ? { ...s, elements: els,
                          characters: (els.characters || []).map(({ name, description }) => ({ name, description })),
                          props: (els.props || []).map(({ name, description }) => ({ name, description })),
                          locations: (els.locations || []).map(({ name, description }) => ({ name, description })) }
                      : s),
                  }))}
                />

                <SuggestedShotsSection
                  sceneId={selectedScene.scene_id}
                  suggestions={selectedSuggestions}
                  suggestionsLoading={!!suggestionsLoading[selectedScene.scene_id]}
                  pendingCount={pendingSuggestionCount}
                  isSuggesting={!!suggesting[selectedScene.scene_id]}
                  actingOnSuggestion={actingOnSuggestion}
                  onSuggest={() => handleSuggestShots(selectedScene.scene_id)}
                  onAcceptAll={() => handleAcceptAll(selectedScene.scene_id)}
                  onAccept={(sid) => handleAcceptSuggestion(selectedScene.scene_id, sid)}
                  onReject={(sid) => handleRejectSuggestion(selectedScene.scene_id, sid)}
                  hasBreakdown={selectedScene.has_breakdown}
                />
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
                <p className="text-sm text-muted-foreground">Select a scene from the left to view its breakdown.</p>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

/* --------------------------- sub-components --------------------------- */

const SceneHeaderCard = ({ scene, filmId, workspaceId, isAnalyzing, onAnalyze }) => {
  const sceneHref = workspaceId
    ? `/workspaces/${workspaceId}/films/${filmId}/scenes/${scene.scene_id}`
    : `/scenes/${scene.scene_id}`;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="aib-scene-header">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 size-12 rounded-xl bg-chart-4/15 flex items-center justify-center">
          <Brain className="size-5 text-chart-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
            Scene {scene.scene_number}
          </p>
          <h2 className="font-display text-2xl font-semibold tracking-tight mt-0.5 truncate">
            {scene.scene_name || scene.raw_slugline || `Scene ${scene.scene_number}`}
          </h2>
          {scene.raw_slugline && (
            <p className="text-sm text-muted-foreground font-mono mt-0.5 truncate">{scene.raw_slugline}</p>
          )}
          {scene.summary && (
            <p className="text-sm text-foreground mt-3 leading-relaxed">{scene.summary}</p>
          )}
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={onAnalyze}
            disabled={isAnalyzing}
            className="rounded-lg"
            data-testid="aib-analyze-btn"
          >
            {isAnalyzing ? (
              <Loader2 className="size-3.5 mr-1.5 animate-spin" />
            ) : scene.has_breakdown ? (
              <RefreshCw className="size-3.5 mr-1.5" />
            ) : (
              <Sparkles className="size-3.5 mr-1.5" />
            )}
            {scene.has_breakdown ? 'Re-analyze' : 'Analyze with AI'}
          </Button>
          <Link
            to={sceneHref}
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1"
          >
            Open scene <ChevronRight className="size-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const BreakdownContent = ({ scene, filmId, canEdit, onElementsSaved }) => {
  const has = (arr) => Array.isArray(arr) && arr.length > 0;
  const elements = elementsFromScene(scene);
  const hasElements = Object.keys(elements).length > 0;
  if (!scene.has_breakdown && !hasElements && !has(scene.key_actions) && !canEdit) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card/40 p-8 text-center" data-testid="aib-not-analyzed">
        <Sparkles className="size-6 text-muted-foreground mx-auto mb-2.5" />
        <p className="font-medium text-foreground">This scene hasn&apos;t been analyzed yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Click <span className="font-medium text-foreground">Analyze with AI</span> above to extract cast,
          props, wardrobe, vehicles, stunts, VFX and more.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <ElementsEditor
        filmId={filmId}
        sceneId={scene.scene_id}
        elements={elements}
        canEdit={canEdit}
        onSaved={onElementsSaved}
      />

      {/* Key actions */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <SectionHeader icon={ListChecks} label="Key Actions" count={scene.key_actions?.length} accent="text-chart-4" />
        {has(scene.key_actions) ? (
          <ol className="space-y-1.5">
            {scene.key_actions.map((a, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <span className="font-mono text-xs text-muted-foreground mt-0.5 flex-shrink-0 w-5">{i + 1}.</span>
                <span className="text-foreground">{a}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground italic">No key actions extracted.</p>
        )}
      </div>
    </div>
  );
};

const SuggestedShotsSection = ({
  sceneId,
  suggestions,
  suggestionsLoading,
  pendingCount,
  isSuggesting,
  actingOnSuggestion,
  onSuggest,
  onAcceptAll,
  onAccept,
  onReject,
  hasBreakdown,
}) => {
  const empty = !suggestions || suggestions.length === 0;

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="aib-suggested-shots">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <SectionHeader icon={Camera} label="Suggested Shot List" count={suggestions.length} accent="text-chart-4" />
          <p className="text-xs text-muted-foreground -mt-1.5 ml-6">
            AI-recommended coverage. Accept the ones you want to add to your shot list.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAcceptAll}
              disabled={isSuggesting}
              className="rounded-lg"
              data-testid="aib-accept-all-btn"
            >
              <Plus className="size-3.5 mr-1.5" /> Add all ({pendingCount})
            </Button>
          )}
          <Button
            size="sm"
            onClick={onSuggest}
            disabled={isSuggesting}
            className="rounded-lg"
            data-testid="aib-suggest-btn"
          >
            {isSuggesting ? (
              <Loader2 className="size-3.5 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="size-3.5 mr-1.5" />
            )}
            {empty ? 'Generate shots' : 'Re-generate'}
          </Button>
        </div>
      </div>

      {suggestionsLoading ? (
        <div className="space-y-2.5">
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
      ) : empty ? (
        <div className="rounded-xl border border-dashed border-border bg-card/40 p-6 text-center">
          <Camera className="size-6 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-foreground font-medium">No shot suggestions yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            {hasBreakdown
              ? 'Click Generate shots to get an AI-suggested shot list for this scene.'
              : 'Analyze the scene first, then generate a shot list.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {suggestions.map((sug) => (
            <ShotSuggestionCard
              key={sug.id}
              suggestion={sug}
              onAccept={onAccept}
              onReject={onReject}
              busy={!!actingOnSuggestion[sug.id]}
            />
          ))}
        </div>
      )}
    </div>
  );
};
